import { useMemo } from 'react'

import { IconPlus, IconRotate, IconSearch } from '@tabler/icons-react'
import { Space, Input, ConfigProvider, TableProps, Tooltip, Badge } from 'antd'
import { ButtonType } from 'antd/lib/button'
import { SorterResult } from 'antd/lib/table/interface'
import { DocumentNode } from 'graphql'
import { isEmpty } from 'lodash'
import { NextRouter, useRouter } from 'next/router'
import styled, { css } from 'styled-components'

import { Button, ButtonProps } from './BUI/Button'
import { DesktopCard } from './DesktopCard'
import { EmptyListBuilderSearch } from './EmptyListBuilderSearch'
import { EmptyListDefault } from './EmptyListDefault'
import { EmptyListWithDateFilter } from './EmptyListWithDateFilter'
import { Flexbox } from './Flexbox'

import ExportableTable, {
  ExportableTableProps,
} from '@/components/shared-components/ExportableTable'
import { accountManagers } from '@/config/constants/roleGroups'
import { useFilters } from '@/context/filterContext'
import { useNavigation } from '@/context/navigationContext'
import { useProject } from '@/context/projectContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useColumnFilters } from '@/hooks/useColumnFilters'
import { useColumnSearch } from '@/hooks/useColumnSearch'
import { useRoles } from '@/utils/ensureRole'
import { isProjectSelected } from '@/utils/index'

import { QuerySupport } from 'types/navigation'

const Search = styled(Input)`
  width: 100%;
`

type SpaceProps = {
  $isMobile?: boolean
}

const StyledSpace = styled(Space)<SpaceProps>`
  width: 100%;
  flex-wrap: wrap;

  & > .ant-space-item:first-of-type {
    width: ${(props) => (props.$isMobile ? '100%' : 'auto')};
  }
`

const CTAContainer = styled(Space)<SpaceProps>`
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      margin-top: 1rem;
    `}
`

const ExtraOptionsContainer = styled(CTAContainer)<SpaceProps>`
  margin-right: 1rem;
`

type CtaButtonProps = ButtonProps & {
  mr?: string
}

const CtaButton = styled(Button)<CtaButtonProps>`
  margin-right: ${({ mr }) => (mr ? mr : '0')};
`

type ExtraCTA = {
  type?: ButtonType
  onClick?: (router: NextRouter) => void
  icon?: React.ReactNode
  disabled?: boolean
  loading?: boolean
  text?: string
}

export type Props = TableProps<object> & {
  children?: React.ReactNode
  resource?: string
  loading?: boolean
  tableData?: object[]
  setSearchInput?: (value: unknown) => void
  csvTransformer?: ExportableTableProps<object>['csvTransformer'] // (value: unknown) => unknown
  pdfQuery?: DocumentNode
  getColumns?: (
    router: NextRouter,
    getColumnSearchProps: unknown,
    tableData: object[],
    refetch: () => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any
  rowKey?: string
  searchable?: boolean
  addCTA?:
    | {
        onClick?: (router: NextRouter) => void
        text?: string
        loading?: boolean
      }
    | (() => React.ReactNode)
  extraCTA?: Array<ExtraCTA>
  extraFilter?: (data: object) => object[]
  rowSelection?: TableProps<unknown>['rowSelection']
  banner?: React.ReactNode
  pagination?: TableProps<unknown>['pagination']
  includeReset?: boolean
  refetch?: () => void
  tableActions?: () => React.ReactNode
  csvQuery?: DocumentNode
  csvExportAllQuery?: DocumentNode
  csvExportAllTransformer?: ExportableTableProps<object>['csvExportAllTransformer']
  dateFilterString?: string
  serverSidePagination?: boolean
  customEmptyComponent?: React.ReactNode
  extraOptions?: React.ReactNode
}

const ListViewDisplay: React.FC<Props> = ({
  resource,
  loading = false,
  tableData,
  setSearchInput,
  csvTransformer,
  getColumns,
  rowKey,
  searchable,
  addCTA,
  extraCTA = null,
  extraFilter,
  rowSelection = null,
  children,
  banner = null,
  pagination = {
    defaultPageSize: 50,
    hideOnSinglePage: true,
  },
  includeReset = true,
  refetch,
  tableActions = () => null,
  dateFilterString,
  onChange,
  serverSidePagination = false,
  customEmptyComponent,
  ...rest
}) => {
  const { currentNavItem } = useNavigation()
  const { project } = useProject()
  const { hasRole } = useRoles()
  const router = useRouter()
  const mobile = useBreakpoint('md')

  const getColumnSearchProps = useColumnSearch()

  const { updateFilters, clearFilters, routeFilters } = useFilters()
  const hasFilters = !isEmpty(routeFilters?.filters)
  const hasSorting = !isEmpty(routeFilters?.sort)
  const hasFiltersOrSorting = hasFilters || hasSorting

  const quantityOfFiltersApplied = hasFiltersOrSorting
    ? Object.keys(routeFilters?.filters || {}).filter(
        (f) => routeFilters?.filters[f] !== null
      ).length
    : 0

  const hasOnlySortingApplied =
    hasFiltersOrSorting && quantityOfFiltersApplied === 0

  const tableColumns = useColumnFilters(
    getColumns(router, getColumnSearchProps, tableData, refetch).map(
      (column) => ({
        ...column,
        ...(column.sorter
          ? { sorter: !serverSidePagination ? column.sorter : true }
          : {}),
        ...(column.onFilter
          ? { onFilter: !serverSidePagination ? column.onFilter : undefined }
          : {}),
      })
    )
  )

  const promoteBuilderSearch =
    tableData?.length === 0 &&
    isProjectSelected(project) &&
    hasRole(accountManagers) &&
    currentNavItem?.querySupport === QuerySupport.BUILDER_AND_PROJECT

  const RenderEmpty = () => {
    const filterString =
      resource && dateFilterString
        ? `No ${resource} ${dateFilterString} found`
        : null

    if (customEmptyComponent) {
      return customEmptyComponent
    }

    if (promoteBuilderSearch) {
      return <EmptyListBuilderSearch extra={filterString} />
    }

    if (filterString) {
      return <EmptyListWithDateFilter filterString={filterString} />
    }

    return <EmptyListDefault resource={resource} />
  }

  const showHeader = searchable || rest?.extraOptions || extraCTA || addCTA

  const actions = useMemo(() => tableActions(), [tableActions])

  return (
    <Space direction="vertical" style={{ display: 'flex' }} size="small">
      {banner && <Flexbox>{banner}</Flexbox>}
      {showHeader && (
        <Flexbox
          mb="0.25rem!important"
          flexDirection={mobile ? 'column' : 'row'}
          justifyContent="between"
        >
          <StyledSpace $isMobile={mobile}>
            {searchable && (
              <Search
                placeholder="Search"
                prefix={<IconSearch size={16} />}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
              />
            )}

            {children}
          </StyledSpace>

          {rest.extraOptions && (
            <ExtraOptionsContainer $isMobile={mobile}>
              {rest.extraOptions}
            </ExtraOptionsContainer>
          )}
          <CTAContainer $isMobile={mobile}>
            {extraCTA &&
              extraCTA.map((cta, i) => {
                const mr: string =
                  i !== extraCTA.length - 1 ? '1rem !important' : ''

                return (
                  <CtaButton
                    mr={mr}
                    key={i}
                    variant={cta.type || 'default'}
                    onClick={() => cta.onClick(router)}
                    icon={cta.icon || null}
                    disabled={cta.disabled}
                    loading={cta.loading}
                    block
                  >
                    {cta.text}
                  </CtaButton>
                )
              })}

            {addCTA && typeof addCTA === 'object' && (
              <Button
                block
                variant="primary"
                onClick={() => addCTA.onClick(router)}
                icon={<IconPlus size={16} />}
                loading={addCTA.loading}
              >
                {addCTA.text}
              </Button>
            )}

            {!!addCTA && typeof addCTA === 'function' && addCTA()}
          </CTAContainer>
        </Flexbox>
      )}

      <DesktopCard
        bodyStyle={{
          paddingTop: '0.75rem',
          ...(rest?.bordered === false && { padding: 0 }),
        }}
        bordered={rest?.bordered}
      >
        {!!includeReset && !!actions && (
          <div style={{ paddingBottom: '1rem' }}>
            <Flexbox justifyContent="space-between">
              <div>{actions}</div>
              {includeReset && (
                <Badge count={quantityOfFiltersApplied} size="default">
                  <Tooltip title="Clears the currently applied filters and sorting">
                    <Button
                      onClick={clearFilters}
                      size="small"
                      disabled={!hasFilters}
                      icon={<IconRotate size={16} />}
                    >
                      Reset {hasOnlySortingApplied ? 'sorting' : 'filters'}
                    </Button>
                  </Tooltip>
                </Badge>
              )}
            </Flexbox>
          </div>
        )}
        <div className="table-responsive">
          <ConfigProvider renderEmpty={RenderEmpty}>
            <ExportableTable
              scroll={{ x: 'max-content' }}
              resource={resource}
              loading={loading}
              columns={tableColumns}
              dataSource={extraFilter ? extraFilter(tableData) : tableData}
              rowKey={rowKey || 'id'}
              rowSelection={rowSelection}
              csvTransformer={csvTransformer}
              onChange={(pagination, filters, sort) => {
                updateFilters({
                  pagination,
                  filters,
                  sort: sort as SorterResult<object>,
                })
                onChange?.(pagination, filters, sort, null)
              }}
              pagination={pagination}
              sortDirections={['ascend', 'descend', 'ascend']} // remove the "false" sort
              {...rest}
            />
          </ConfigProvider>
        </div>
      </DesktopCard>
    </Space>
  )
}

export default ListViewDisplay
